import React, { useState, useMemo } from 'react';
import { Star, Search } from 'lucide-react';
import {
  LAYOUT_STYLES, TEMPLATE_CATEGORIES, PIN_PALETTES,
  buildTemplateLayers, getRecommendedTemplates
} from '../../data/pinTemplates';

const PREVIEW_SCALE = 0.18;
const PW = 1000 * PREVIEW_SCALE;
const PH = 1500 * PREVIEW_SCALE;

// Mini pin renderer for template previews
const MiniPinPreview = ({ layers, scale = PREVIEW_SCALE }) => (
  <div className="relative overflow-hidden bg-white" style={{ width: PW, height: PH, borderRadius: 6 }}>
    {layers.map((layer) => {
      const x = (layer.x || 0) * scale;
      const y = (layer.y || 0) * scale;
      const lw = (layer.w || 0) * scale;
      const lh = (layer.h || 0) * scale;

      if (layer.type === 'rect') {
        return (
          <div key={layer.id} className="absolute" style={{
            left: x, top: y, width: lw, height: lh,
            background: layer.fill || 'transparent',
            borderRadius: (layer.radius || 0) * scale,
            border: layer.stroke ? `${Math.max((layer.strokeWidth || 1) * scale, 0.5)}px solid ${layer.stroke}` : 'none',
          }} />
        );
      }
      if (layer.type === 'text') {
        return (
          <div key={layer.id} className="absolute overflow-hidden" style={{
            left: x, top: y, width: lw,
            fontFamily: layer.font || 'Inter',
            fontSize: Math.max((layer.fontSize || 16) * scale, 3),
            fontWeight: layer.fontWeight || '400',
            color: layer.color || '#111',
            textAlign: layer.align || 'left',
            lineHeight: layer.lineHeight || 1.3,
            letterSpacing: layer.letterSpacing ? layer.letterSpacing * scale : undefined,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}>
            {layer.text}
          </div>
        );
      }
      if (layer.type === 'button') {
        return (
          <div key={layer.id} className="absolute flex items-center justify-center" style={{
            left: x, top: y, width: lw, height: lh,
            background: layer.fill || '#B49A88',
            borderRadius: (layer.radius || 4) * scale,
            fontSize: Math.max((layer.fontSize || 16) * scale, 3),
            fontWeight: layer.fontWeight || '600',
            fontFamily: layer.font || 'Inter',
            color: layer.color || '#fff',
            border: layer.stroke ? `${Math.max((layer.strokeWidth || 1) * scale, 0.5)}px solid ${layer.stroke}` : 'none',
          }}>
            {layer.text}
          </div>
        );
      }
      return null;
    })}
  </div>
);

const EXAMPLE_CONTENT = {
  editorial_luxury: { headline: 'The Art of Wedding Planning', subheadline: 'Everything you need, beautifully organized', cta: 'Discover' },
  scandinavian_minimal: { headline: 'Simple. Clean. Organized.', subheadline: 'A minimal approach to planning', cta: 'Learn more' },
  magazine_editorial: { headline: 'Your Dream Wedding Starts Here', subheadline: 'The complete planner for modern couples', cta: 'Read more' },
  clean_product: { headline: 'Wedding Planner Bundle', subheadline: '30+ pages of planning templates', cta: 'Get it now' },
  bold_typography: { headline: 'STOP FORGETTING DETAILS', subheadline: 'Every wedding detail in one place', cta: 'Start free' },
  checklist: { headline: '25 Things Couples Forget', subheadline: 'The ultimate wedding checklist', cta: 'Get the list' },
  listicle: { headline: '10 Wedding Budget Secrets', subheadline: 'Save thousands on your big day', cta: 'Read now' },
  how_to: { headline: 'Plan Your Wedding in 90 Days', subheadline: 'A step-by-step guide', cta: 'Get started' },
  problem_solution: { headline: 'Never Miss a Wedding Detail Again', subheadline: 'From chaos to confidence', cta: 'Try it free' },
  educational_card: { headline: 'Did You Know?', subheadline: 'Most couples forget these 5 wedding costs', cta: 'Learn more' },
  before_after: { headline: 'Transform Your Wedding Planning', subheadline: 'From stress to organized bliss', cta: 'See how' },
  soft_feminine: { headline: 'Plan Your Perfect Day', subheadline: 'A beautiful way to organize your wedding', cta: 'Explore' },
  neutral_organic: { headline: 'Natural Wedding Planning', subheadline: 'Simple tools for a beautiful celebration', cta: 'Start here' },
  modern_business: { headline: 'The Smart Wedding Planner', subheadline: 'Professional tools for the modern couple', cta: 'Get access' },
  romantic: { headline: 'Your Love Story Deserves a Beautiful Plan', subheadline: 'Every detail, thoughtfully organized', cta: 'Begin' },
  dark_luxury: { headline: 'Luxury Wedding Planning', subheadline: 'Elevated tools for your most important day', cta: 'Discover' },
  collage: { headline: 'Everything in One Place', subheadline: 'Budget, guest list, timeline & more', cta: 'Get it now' },
  quote_inspiration: { headline: 'The best wedding is one that feels like you', subheadline: 'Start planning your way', cta: 'Get inspired' },
  photography_led: { headline: 'Capture Every Moment', subheadline: 'Your wedding photography checklist', cta: 'Download' },
};

const TemplatePicker = ({ productCategory, marketingAngle, onSelect, selectedId }) => {
  const [filter, setFilter] = useState('recommended');
  const [search, setSearch] = useState('');

  const recommended = useMemo(
    () => getRecommendedTemplates(productCategory, marketingAngle, 19),
    [productCategory, marketingAngle]
  );

  const allTemplates = useMemo(() => {
    return Object.entries(LAYOUT_STYLES).map(([id, meta]) => {
      const content = EXAMPLE_CONTENT[id] || { headline: 'Your Headline', subheadline: 'Supporting text', cta: 'Learn more' };
      const palette = id === 'dark_luxury' ? PIN_PALETTES[3] : PIN_PALETTES[0];
      const layers = buildTemplateLayers(id, palette, content);
      const isRecommended = recommended.indexOf(id) < 6;
      const rank = recommended.indexOf(id);
      return { id, ...meta, layers, content, isRecommended, rank };
    });
  }, [recommended]);

  const filtered = useMemo(() => {
    let items = [...allTemplates];

    if (search) {
      const q = search.toLowerCase();
      items = items.filter((t) =>
        t.label.toLowerCase().includes(q) ||
        t.tags.some((tag) => tag.includes(q)) ||
        t.category.includes(q)
      );
    } else if (filter === 'recommended') {
      items.sort((a, b) => a.rank - b.rank);
    } else if (filter !== 'all') {
      items = items.filter((t) => t.category === filter);
    }

    return items;
  }, [allTemplates, filter, search]);

  return (
    <div data-testid="template-picker">
      {/* Filter tabs */}
      <div className="flex items-center gap-2 mb-4 overflow-x-auto scrollbar-hide pb-1">
        {TEMPLATE_CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            onClick={() => { setFilter(cat.id); setSearch(''); }}
            className={`px-3 py-1.5 rounded-full text-[10px] font-sans font-semibold whitespace-nowrap transition-all ${
              filter === cat.id && !search
                ? 'bg-stone-900 text-white'
                : 'bg-white border border-stone-100 text-stone-500 hover:border-stone-200'
            }`}
          >
            {cat.label}
          </button>
        ))}
        <div className="relative flex-1 min-w-[120px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-stone-300" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            className="w-full pl-7 pr-3 py-1.5 border border-stone-100 rounded-full text-[10px] font-sans text-stone-600 placeholder:text-stone-300 focus:outline-none focus:border-[#B49A88]"
          />
        </div>
      </div>

      {/* Template grid */}
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3">
        {filtered.map((t) => (
          <button
            key={t.id}
            data-testid={`template-${t.id}`}
            onClick={() => onSelect(t.id)}
            className={`relative rounded-xl overflow-hidden border-2 transition-all hover:-translate-y-0.5 hover:shadow-md ${
              selectedId === t.id
                ? 'border-[#E60023] shadow-md'
                : 'border-transparent hover:border-stone-200'
            }`}
          >
            <div className="bg-stone-50 p-2 flex justify-center">
              <MiniPinPreview layers={t.layers} />
            </div>
            <div className="p-1.5 bg-white">
              <p className="font-sans font-semibold text-stone-700 text-[9px] truncate">{t.label}</p>
              {t.isRecommended && filter === 'recommended' && (
                <div className="flex items-center gap-0.5 mt-0.5">
                  <Star className="w-2.5 h-2.5 text-amber-400 fill-amber-400" />
                  <span className="text-[8px] font-sans text-amber-600">Anbefalt</span>
                </div>
              )}
            </div>
            {selectedId === t.id && (
              <div className="absolute top-1.5 right-1.5 w-4 h-4 bg-[#E60023] rounded-full flex items-center justify-center">
                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export default TemplatePicker;
